import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { toast } from 'sonner';

export interface RecipeEditorProps {
  initialYaml: string;
  sessionId: string;
  onSaved: (newYaml: string) => void;
  onCancel: () => void;
}

export function RecipeEditor({ initialYaml, sessionId, onSaved, onCancel }: RecipeEditorProps) {
  const [draft, setDraft] = useState(initialYaml);
  const qc = useQueryClient();
  const dirty = draft !== initialYaml;

  const save = useMutation({
    mutationFn: () => api.editScopeRecipe(sessionId, draft),
    onSuccess: (data) => {
      if (data.unchanged) {
        toast.info('No changes to save');
      } else {
        toast.success('Recipe saved as new version');
      }
      void qc.invalidateQueries({ queryKey: ['sessions', sessionId, 'scope-recipe'] });
      void qc.invalidateQueries({ queryKey: ['sessions', sessionId] });
      onSaved(draft);
    },
    onError: (err) => toast.error(`Save failed: ${err instanceof Error ? err.message : String(err)}`),
  });

  return (
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--glass-border)' }}>
      <div className="flex items-center justify-between px-4 py-2 border-b border-border">
        <div className="flex items-center gap-3">
          <span className="text-xs font-semibold uppercase tracking-widest text-text-dimmed">Edit Recipe</span>
          {dirty && (
            <span
              className="text-[10px] font-mono px-1.5 py-0.5 rounded"
              style={{ background: 'rgba(250,204,21,0.18)', color: 'rgb(254,240,138)' }}
            >
              unsaved
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg px-3 py-1.5 text-xs font-semibold transition-opacity"
            style={{ background: 'rgba(148,163,184,0.18)', color: 'var(--text-dimmed)', border: '1px solid rgba(148,163,184,0.3)' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => save.mutate()}
            disabled={save.isPending || !dirty}
            className="rounded-lg px-3 py-1.5 text-xs font-semibold transition-opacity disabled:opacity-50"
            style={{
              background: 'rgba(56,189,248,0.18)',
              color: 'rgb(125,211,252)',
              border: '1px solid rgba(56,189,248,0.45)',
            }}
          >
            {save.isPending ? 'Saving…' : '💾 Save Version'}
          </button>
        </div>
      </div>
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        spellCheck={false}
        wrap="off"
        style={{
          width: '100%',
          minHeight: 360,
          maxHeight: 600,
          background: 'var(--bg-primary)',
          color: 'var(--text-secondary)',
          border: 'none',
          padding: 16,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: 12,
          lineHeight: 1.5,
          resize: 'vertical',
          outline: 'none',
          tabSize: 2,
        }}
      />
    </div>
  );
}
